import { TaxType } from '@wix/ambassador-events-v1-event/types';
import { TicketSaleStatus as AmbassadorTicketSaleStatus, } from '@wix/ambassador-events-v1-ticket-definition/types';
import { PricingTypeEnumType, SaleStatusEnumStatus, } from '@wix/ambassador-events-v3-ticket-definition/types';
import { TicketSaleStatus } from '@wix/events-types';
import { WIX_FEE_RATE } from '../constants/event';
import { WIX_EVENTS_TICKET_DEFINITION_FQDN, WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS } from '../constants/fqdn';
import { getFormattedMoney } from '../price/formatter';
import { getMoney } from '../price/utils';
import { getPreliminaryTax, isFeeAdded, isTaxAdded } from './tax';
export const isTicketDefinitionV3 = (definition) => definition.revision !== undefined;
export const calculateTicketPriceBreakDown = ({ ticketPrice, feeType, taxConfig, isDonation = false, getFormattedMoneyFunction = getFormattedMoney, }) => {
    const { currency } = ticketPrice;
    const feeAdded = isFeeAdded(feeType);
    const taxIncluded = taxConfig?.type === TaxType.INCLUDED;
    const originalTicketPrice = Number(ticketPrice.amount || 0);
    let ticketPriceAmount = originalTicketPrice;
    const tax = getPreliminaryTax(taxConfig, ticketPriceAmount, currency, isDonation);
    const taxAmount = getTaxAmount(tax);
    ticketPriceAmount = getTicketPriceAfterTax(ticketPriceAmount, tax);
    const feeAmount = Number((ticketPriceAmount * WIX_FEE_RATE).toFixed(2));
    const totalBuyerPaysAmount = feeAdded ? ticketPriceAmount + feeAmount : ticketPriceAmount;
    const getPaidAmount = feeAdded ? ticketPriceAmount : totalBuyerPaysAmount - feeAmount;
    const totalBuyerPays = getFormattedMoneyFunction(getMoney(totalBuyerPaysAmount, currency));
    const getPaid = getFormattedMoneyFunction(getMoney(getPaidAmount, currency));
    const fee = getFormattedMoneyFunction(getMoney(feeAmount, currency));
    const taxFormatted = tax ? getFormattedMoneyFunction(tax.amount) : null;
    const formattedTicketPrice = getFormattedMoneyFunction(taxIncluded ? getMoney(originalTicketPrice - taxAmount, currency) : ticketPrice);
    return {
        totalBuyerPays,
        fee,
        getPaid,
        ticketPrice: formattedTicketPrice,
        tax: taxFormatted,
    };
};
export const getTicketServiceFee = (definition, taxConfig, overridePrice, getFormattedMoneyFunction = getFormattedMoney) => {
    const ticketPrice = Number(overridePrice ?? definition.price.amount);
    const tax = getPreliminaryTax(taxConfig, ticketPrice, getTicketDefinitionCurrency(definition), isDonationTicketDefinition(definition));
    const ticketPriceAmount = getTicketPriceAfterTax(ticketPrice, tax);
    return getFormattedMoneyFunction({
        amount: (ticketPriceAmount * WIX_FEE_RATE).toString(),
        currency: getTicketDefinitionCurrency(definition),
    });
};
export const getTicketTax = (definition, taxConfig, overridePrice, getFormattedMoneyFunction = getFormattedMoney) => getFormattedMoneyFunction(getPreliminaryTax(taxConfig, Number(overridePrice ?? definition.price.amount), getTicketDefinitionCurrency(definition), isDonationTicketDefinition(definition)).amount);
export const getTaxRateLabel = (taxConfig) => (taxConfig ? `${taxConfig.rate}%` : null);
const getTaxAmount = (tax) => Number(tax?.amount?.amount ?? 0);
const getTicketPriceAfterTax = (ticketPrice, tax) => ticketPrice + (isTaxAdded(tax?.type) ? getTaxAmount(tax) : 0);
export const getHideNotOnSale = (definition) => {
    const hideNotOnSale = isTicketDefinitionV3(definition)
        ? !(definition.salePeriod?.displayNotOnSale ?? true)
        : definition.salePeriod?.hideNotOnSale;
    return hideNotOnSale ?? null;
};
export const shouldTicketLimitTooltipShow = (availableTicketsQuantity, selectedQuantity) => availableTicketsQuantity === 0 && !!selectedQuantity;
export const saleScheduled = (definition) => definition.saleStatus === TicketSaleStatus.SALE_SCHEDULED ||
    definition.saleStatus === AmbassadorTicketSaleStatus.SALE_SCHEDULED ||
    definition.saleStatus === SaleStatusEnumStatus.SALE_SCHEDULED;
export const saleStarted = (definition) => definition.saleStatus === TicketSaleStatus.SALE_STARTED ||
    definition.saleStatus === AmbassadorTicketSaleStatus.SALE_STARTED ||
    definition.saleStatus === SaleStatusEnumStatus.SALE_STARTED;
export const saleEnded = (definition) => definition.saleStatus === TicketSaleStatus.SALE_ENDED ||
    definition.saleStatus === AmbassadorTicketSaleStatus.SALE_ENDED;
export const shouldShowSaleEndsLabel = (definition) => saleStarted(definition) && !!definition.limitPerCheckout && definition.salePeriod;
export const saleStartedAndHasTickets = (definition) => saleStarted(definition) && !!definition.limitPerCheckout;
export const saleEndedAndHasTickets = (definition) => saleEnded(definition) && !!definition.limitPerCheckout;
export const saleScheduledAndHasTickets = (definition) => saleScheduled(definition) && !!definition.limitPerCheckout;
export const externalIdToTicketDefinitionId = (externalId = '') => externalId.replace(WIX_EVENTS_TICKET_DEFINITION_FQDN, '').replace(WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS, '');
export const ticketDefinitionIdToExternalId = (ticketDefinitionId) => `${WIX_EVENTS_TICKET_DEFINITION_FQDN}${ticketDefinitionId}`;
export const ticketDefinitionIdToExternalIdAllVersions = (ticketDefinitionId) => `${WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS}${ticketDefinitionId}`;
export const isTicketDefinitionHidden = (definition) => {
    const hidden = isTicketDefinitionV3(definition) ? definition.hidden : definition.dashboard?.hidden;
    return hidden || (getHideNotOnSale(definition) && !saleStarted(definition));
};
export const isTicketDefinitionSoldOut = (definition) => {
    if (isTicketDefinitionV3(definition)) {
        return definition.salesDetails?.soldOut && definition.salesDetails?.unsoldCount <= 0;
    }
    return definition.dashboard.limited && definition.dashboard.unsold <= 0;
};
export const isTicketDefinitionManuallyHidden = (definition) => isTicketDefinitionV3(definition) ? definition.hidden : definition.dashboard?.hidden;
export const isFreeTicketDefinition = (definition) => {
    if (isTicketDefinitionV3(definition)) {
        return definition.pricingMethod?.free;
    }
    else if (hasPricingOptions(definition)) {
        return false;
    }
    else {
        return definition.free;
    }
};
export const isDonationTicketDefinition = (definition) => isTicketDefinitionV3(definition)
    ? definition.pricingMethod.pricingType === PricingTypeEnumType.DONATION
    : definition.pricing?.minPrice !== undefined;
export const getTicketPrice = (definition) => isTicketDefinitionV3(definition) ? definition.pricingMethod?.fixedPrice : definition.price;
export const minimumDonation = (definition) => {
    if (isDonationTicketDefinition(definition)) {
        return Number(isTicketDefinitionV3(definition)
            ? definition.pricingMethod?.guestPrice?.value
            : definition.pricing?.minPrice?.value);
    }
    return undefined;
};
export const getTicketDefinitionPolicy = (definition) => isTicketDefinitionV3(definition) ? definition.policyText : definition.policy;
export const getTicketDefinitionCurrency = (definition) => {
    if (isTicketDefinitionV3(definition)) {
        const { pricingMethod } = definition;
        return (pricingMethod?.fixedPrice?.currency ??
            pricingMethod?.pricingOptions?.optionDetails?.[0]?.price?.currency ??
            pricingMethod?.guestPrice?.currency);
    }
    return isDonationTicketDefinition(definition) ? definition.pricing?.minPrice?.currency : definition.price?.currency;
};
export const isDonationLessThanMinimum = (definition, donation) => {
    const minimum = minimumDonation(definition);
    return minimum && donation < minimum;
};
export const isAvailableDonationTicket = (definition) => isDonationTicketDefinition(definition) && saleStartedAndHasTickets(definition);
export const getPricingOptions = (definition) => definition.pricing?.pricingOptions?.options ?? [];
export const getPricingOptionsV3 = (definition) => definition.pricingMethod?.pricingOptions?.optionDetails ?? [];
export const hasPricingOptions = (definition) => (isTicketDefinitionV3(definition) ? getPricingOptionsV3(definition) : getPricingOptions(definition)).length > 0;
export const hasMultiplePricingOptions = (definition) => (isTicketDefinitionV3(definition) ? getPricingOptionsV3(definition) : getPricingOptions(definition)).length > 1;
export const hasSinglePricingOption = (definition) => (isTicketDefinitionV3(definition) ? getPricingOptionsV3(definition) : getPricingOptions(definition)).length === 1;
export const getPricingOptionsRange = (definition) => getSelectedPricingOptionsRange(isTicketDefinitionV3(definition) ? getPricingOptionsV3(definition) : getPricingOptions(definition));
export const isPricingOptionsWithSamePrice = (definition) => {
    const { min, max } = getPricingOptionsRange(definition);
    return min.value === max.value;
};
export const getSelectedPricingOptionsRange = (pricingOptions) => pricingOptions.reduce((range, { price }) => {
    const amount = Number(price.value);
    if (range.min === undefined || Number(range.min.amount) > amount) {
        range.min = price;
    }
    if (range.max === undefined || Number(range.max.amount) < amount) {
        range.max = price;
    }
    return range;
}, { min: undefined, max: undefined });
export const getPricingOption = (definition, optionId) => getPricingOptions(definition)?.find(pricingOption => pricingOption.id === optionId);
export const getPricingOptionV3 = (definition, optionId) => getPricingOptionsV3(definition)?.find(pricingOption => pricingOption.optionId === optionId);
export const getPricingOptionPriceV3 = (definition, optionId) => getPricingOptionV3(definition, optionId)?.price.value;
export const getPricingOptionLabel = (definition, optionId, getFormattedMoneyFunction = getFormattedMoney) => {
    const pricingOption = isTicketDefinitionV3(definition)
        ? getPricingOptionV3(definition, optionId)
        : getPricingOption(definition, optionId);
    return `${pricingOption.name} ${getFormattedMoneyFunction(pricingOption.price)}`;
};
export const getFormattedMultiplePrices = (definition, getFormattedMoneyFunction = getFormattedMoney, separator = '-') => {
    const options = isTicketDefinitionV3(definition) ? getPricingOptionsV3(definition) : getPricingOptions(definition);
    if (options?.length) {
        const currency = options[0].price.currency;
        const priceAmounts = options.map(option => Number(option.price.value));
        const minPriceAmount = Math.min(...priceAmounts);
        const maxPriceAmount = Math.max(...priceAmounts);
        const formattedMinPriceAmount = getFormattedMoneyFunction(getMoney(minPriceAmount, currency));
        const formattedMaxPriceAmount = getFormattedMoneyFunction(getMoney(maxPriceAmount, currency));
        if (minPriceAmount === maxPriceAmount) {
            return formattedMinPriceAmount;
        }
        else {
            return `${formattedMinPriceAmount}${separator}${formattedMaxPriceAmount}`;
        }
    }
    else {
        return undefined;
    }
};
//# sourceMappingURL=ticket-definition.js.map